import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import FichaFinanceiraService from "../../services/FichaFinanceiraService";
import {
  BlueTitle,
  ButtonGroupStart,
  CloseButtonWrapper,
  LoadingGif,
  LoadingOverlay,
  ModalBody,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledHR,
} from "../../shared/styles/global.styles";
import Button from "../Button/button";
import { IoCloseSharp } from "react-icons/io5";
import { Col, Row } from "react-bootstrap";
import { Label } from "../Label/styles";
import { GetTipoExclusaoDTO } from "../../shared/dtos/TipoExclusao.dto";
import tipoExclusaoService from "../../services/TipoExclusaoService";
import SelectComponent from "../Select/select";
import loadSCGif from "../../assets/load_SC.gif";
import { GetFinancialRecordDto } from "../../shared/dtos/FichaFinanceira.dto";
import {
  StatusImpressaoEnum,
  StatusRemessaBaixaEnum,
  StatusRemessaEnum,
} from "../../shared/enum/fichaFinanceira.enum";

interface Props {
  show: boolean;
  onClose: () => void;
  installments: GetFinancialRecordDto[];
  fetchAllParcelas: () => void;
}

const CancelInstallmentsModal: React.FC<Props> = ({
  show,
  onClose,
  installments,
  fetchAllParcelas,
}) => {
  const [exclusionTypes, setExclusionTypes] = useState<GetTipoExclusaoDTO[]>(
    []
  );
  const [selectedExclusion, setSelectedExclusion] =
    useState<GetTipoExclusaoDTO>();
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const inputRefs = useRef<{
    [key: string]: HTMLInputElement | HTMLSelectElement | null;
  }>({
    selectedExclusion: null,
  });

  const fetchExclusionTypes = async () => {
    try {
      const response = await tipoExclusaoService.getAll();
      setExclusionTypes(response);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao buscar tipos de exclusão",
      });
    }
  };

  const handleClose = () => {
    setInvalidFields([]);
    setSelectedExclusion(undefined);
    onClose();
  };

  const handleSubmit = async () => {
    // Lista de campos obrigatórios
    const camposObrigatorios: {
      name: string;
      label: string;
    }[] = [{ name: "selectedExclusion", label: "Motivo do cancelamento" }];

    const camposNaoPreenchidos = camposObrigatorios.filter((campo) => {
      // Verifica se o campo é um select e se o valor selecionado é null
      if (inputRefs.current[campo.name] instanceof HTMLSelectElement) {
        return inputRefs.current[campo.name]?.value === "";
      }
      return !selectedExclusion;
    });

    if (camposNaoPreenchidos.length > 0) {
      const invalidFieldNames = camposNaoPreenchidos.map((campo) => campo.name);
      setInvalidFields(invalidFieldNames);

      Swal.fire({
        title: "Campos Obrigatórios",
        text: "Por favor, preencha todos os campos obrigatórios.",
        icon: "warning",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      }).then(() => {
        // Foca no primeiro campo inválido
        const firstInvalidField = invalidFieldNames[0];
        if (inputRefs.current[firstInvalidField]) {
          inputRefs.current[firstInvalidField]?.focus();
        }
      });

      return;
    }

    const installmentsToCancel = installments;
    const numberOfInstallments = installmentsToCancel.length;
    const text = `Deseja realmente cancelar ${numberOfInstallments} parcela(s)?`;
    const confirm = await Swal.fire({
      title: text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Não",
      allowOutsideClick: false,
    });
    if (!confirm.isConfirmed) {
      handleClose();
      return;
    }

    const sentInstallments = installmentsToCancel.filter(
      (installment) =>
        installment.statusParcela === "Ativa" &&
        installment.statusRemessa === "Enviada" &&
        !!installment.numeroRemessa &&
        !installment.numeroRetorno
    );

    if (sentInstallments.length > 0) {
      const installmentNumbers = sentInstallments
        .map((inst) => inst.sequencialParcela)
        .join(", ");

      Swal.fire({
        title: "Atenção",
        text: `Existe(m) ${sentInstallments.length} parcela(s) que já foi(ram) enviada(s) ao banco: ${installmentNumbers}. Deseja cancelar a parcela(s) enviada(s) ao banco?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // TODO: implementar cancelamento de parcelas enviadas ao banco
        }
      });
      return;
    }

    Swal.fire({
      title: "Aguarde",
      text: "Cancelando os dados...",
      icon: "info",
      showCancelButton: false,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    try {
      const response = await FichaFinanceiraService.cancelInstallments(
        installmentsToCancel.map((installment) => installment.id),
        Number(selectedExclusion?.id)
      );
      if (response) {
        Swal.close();
        const installmentsToSend = installmentsToCancel.filter(
          (installment) =>
            installment.statusRemessa === StatusRemessaEnum.ENVIADA
        );

        const firstResult = await Swal.fire({
          icon: "success",
          title: "Sucesso",
          text: "Cancelamento concluido",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
        if (firstResult.isConfirmed) {
          fetchAllParcelas();
          handleClose();
          if (firstResult.isConfirmed && installmentsToSend.length > 0) {
            const result = await Swal.fire({
              title: "Enviar cancelamento na remessa?",
              text: `Existem ${installmentsToSend.length} parcela(s) que já foi(ram) enviada(s) ao banco. Deseja enviar na remessa como cancelamento?`,
              inputOptions: {
                [StatusRemessaBaixaEnum.ENVIAR]: "Enviar",
                [StatusRemessaBaixaEnum.NAO_ENVIAR]: "Não enviar",
              },
              input: "select",
              icon: "info",
              inputValue: StatusRemessaBaixaEnum.ENVIAR, // Define o status selecionado como padrão
              showCancelButton: true,
              confirmButtonText: "Sim",
              cancelButtonText: "Cancelar",
              allowOutsideClick: false,
            });
            if (result.isConfirmed) {
              Swal.fire({
                title: "Aguarde",
                text: "Atualizando fichas...",
                icon: "info",
                showCancelButton: false,
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
              });
              installmentsToSend.map(async (installment) => {
                await FichaFinanceiraService.update(installment.id, {
                  statusRemessaBaixa: result.value,
                }).then(() => {
                  Swal.fire({
                    icon: "success",
                    title: "Sucesso",
                    text: "Ficha(s) enviada(s) atualizada(s) com sucesso",
                    confirmButtonText: "OK",
                    allowOutsideClick: false,
                  });
                });
              });
            }
          } else {
            Swal.close();
          }
        }
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Erro ao salvar dados",
        text: "Ocorreu um erro ao salvar os dados. Por favor, tente novamente.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
    }
  };

  useEffect(() => {
    fetchExclusionTypes();
    setLoading(false);
  }, []);

  return (
    <ModalContainer show={show}>
      {loading ? (
        <LoadingOverlay>
          <LoadingGif src={loadSCGif} alt="Carregando..." />
        </LoadingOverlay>
      ) : (
        <ModalContent>
          <Button
            type="button"
            schemaButton="x-close"
            action={() => handleClose()}
            icon={<IoCloseSharp size={30} />}
            position="absolute"
            top={10}
            right={10}
          />

          <ModalHeader>
            <BlueTitle>Cancelamento de Parcelas</BlueTitle>
          </ModalHeader>
          <ModalBody width={30}>
            <Row>
              <Col>
                <Label>Motivo do cancelamento</Label>
                <SelectComponent
                  color="black"
                  placeholder="Selecione"
                  id="selectedExclusion"
                  width={Number("100%")}
                  name="selectedExclusion"
                  value={selectedExclusion?.id?.toString() ?? ""}
                  onChange={(e) => {
                    const selectedExclusion = exclusionTypes.find(
                      (exclusiontype) =>
                        exclusiontype.id === parseInt(e.target.value)
                    );
                    setSelectedExclusion(selectedExclusion);
                    setInvalidFields((prev) =>
                      prev.filter((field) => field !== "selectedExclusion")
                    );
                  }}
                  options={exclusionTypes.map((exclusionType) => ({
                    value: exclusionType.id!.toString(),
                    label: exclusionType.nome!,
                  }))}
                  errorMessage={
                    invalidFields.includes("selectedExclusion")
                      ? "A seleção do motivo do cancelamento é obrigatória"
                      : ""
                  }
                />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              schemaButton="full-primary"
              action={handleSubmit}
              disabled={isRequest || invalidFields.length > 0}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
      ;
    </ModalContainer>
  );
};

export default CancelInstallmentsModal;
